import { z } from 'zod';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import type { WholesalerConfig } from '@/types/services';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';
import { useInvalidateNotifications } from '@/hooks/use-invalidate-notifications';

export const UpdateWholesalerConfigSchema = z.object({
  wholesalerId: z.string().min(1),
  config: z.object({
    serviceConfigs: z.array(
      z.object({
        serviceId: z.string(),
        visibility: z.enum(['off', 'wholesaler', 'partner', 'company']),
      }),
    ),
    featureConfigs: z.array(z.object({ featureId: z.string(), enabled: z.boolean() })),
    carriagePreference: z.array(z.string()),
    countryCarriage: z.array(z.object({ countryCode: z.string(), carriage: z.array(z.string()) })),
  }),
});
export type UpdateWholesalerConfigPayload = z.infer<typeof UpdateWholesalerConfigSchema>;

const fetchConfig = async (wholesalerId: string): Promise<WholesalerConfig> => {
  const { data } = await api.get<WholesalerConfig>(API_ROUTES.services.config(wholesalerId));
  return data;
};

const updateConfig = async (
  wholesalerId: string,
  config: WholesalerConfig,
): Promise<WholesalerConfig> => {
  const { config: validatedConfig } = UpdateWholesalerConfigSchema.parse({ wholesalerId, config });
  const { data } = await api.put<WholesalerConfig>(
    API_ROUTES.services.config(wholesalerId),
    validatedConfig,
  );
  return data;
};

export const getConfigQueryOptions = (wholesalerId: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.services.config(wholesalerId),
    queryFn: () => fetchConfig(wholesalerId),
  });

export const useWholesalerConfig = (wholesalerId: string) =>
  useQuery({ ...getConfigQueryOptions(wholesalerId), enabled: !!wholesalerId });

export const useUpdateWholesalerConfig = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();

  return useMutation({
    mutationFn: ({ wholesalerId, config }: { wholesalerId: string; config: WholesalerConfig }) =>
      updateConfig(wholesalerId, config),
    onSuccess: (data, { wholesalerId }) => {
      queryClient.setQueryData(QUERY_KEYS.services.config(wholesalerId), data);
      invalidateNotifications();
      toaster.create({ type: 'success', title: DICTIONARY.servicesPage.toast.configSaved });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.servicesPage.toast.configError,
        description: getApiErrorMessage(error),
      });
    },
  });
};
